import { createApi } from '@reduxjs/toolkit/query/react';

import { Notistack } from '@/app/shared/components/Notistack';
import { baseQuery } from '@/store/api/baseQuery';
import { setLoading } from '@/store/reducers/authSlice';




export interface IOption {
  id: string;
  name: string;
  amount: number;
  isSurcharge: boolean;
}

export interface IPlan {
  planId: string;
  planName: string;
}

export interface IRate {
  rateId: string;
  minTerm: number;
  maxTerm: number | null;
  maxMiles: number | null;
}

export interface IDeductible {
  id: string;
  name: string;
  amount: number;
}

export interface IProduct {
  cost: number;
  dealProductId: string;
  plan: IPlan;
  rate: IRate;
  deductible: IDeductible;
  retail: number;
  options: IOption[];
  productTypeName: string;
  isSelected: boolean;
  isContracted: boolean;
  isHighlighted?: boolean;
}

export interface ICustomerCard {
  cardName: string;
  isSelected: boolean;
  products: IProduct[];
}

export interface ICustomerProductsResponse {
  id: string;
  dealId: string;
  contactId: string;
  customerCards: ICustomerCard[];
  createdAt: string | null;
  updatedAt: string | null;
  term: number;
  rate: number;
  success: boolean;
  message?: string;
  isContracted: boolean;
}

export interface IProductDetails {
  planName: string;
  productTypeName: string;
  termsPerMiles: string;
  deductible: string;
  iLendingRetail: number;
}

interface ISubmitProducts {
  dealProductId: string;
  isSelected: boolean;
}
export interface ICustomerSubmitCard {
  cardName: string;
  isSelected: boolean;
  products: ISubmitProducts[];
}
interface IUpdateCustomerRequest {
  dealId: string;
  contactId: string;
  customerCards: ICustomerSubmitCard[] | null;
  isFromPhoenix: boolean;
}

interface IRejectAllProductsRequest {
  id: string;
  isRejectAll: boolean;
  isFromPhoenix: boolean;
}

export const productsApi = createApi({
  reducerPath: 'productsApi',

  baseQuery,

  tagTypes: ['Products'],

  endpoints: (builder) => ({
    fetchCustomerProducts: builder.query<ICustomerProductsResponse, string>({
      query: (contactId) => ({
        url: `/contacts/${contactId}/products`,
        method: 'GET',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setLoading(true));
        try {
          await queryFulfilled;
          dispatch(setLoading(false));
        } catch {
          dispatch(setLoading(false));
        }
      },
      providesTags: ['Products'],
    }),
    updateCustomerProducts: builder.mutation<ICustomerProductsResponse, { id: string; payload: IUpdateCustomerRequest }>({
      query: ({ id, payload }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: payload,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setLoading(true));
        try {
          await queryFulfilled;
          dispatch(setLoading(false));
          Notistack.successMessage('Products submitted successfully');
        } catch {
          dispatch(setLoading(false));
          Notistack.generalError('Failed to submit products');
        }
      },
    }),
    rejectAllProducts: builder.mutation<void, IRejectAllProductsRequest>({
      query: (payload) => ({
        url: 'reject-all',
        method: 'PATCH',
        body: payload,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setLoading(true));
        try {
          await queryFulfilled;
          dispatch(setLoading(false));
          Notistack.successMessage('Declined products successfully');
        } catch {
          dispatch(setLoading(false));
          Notistack.generalError('Failed to decline products');
        }
      },
    }),
  }),
});

export const { useFetchCustomerProductsQuery, useUpdateCustomerProductsMutation, useRejectAllProductsMutation } = productsApi;
